import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  CalendarRange,
  Flame,
  Microscope,
  Telescope,
} from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { KpiCard } from "@/components/kpi-card";
import { RunsTable } from "@/components/runs-table";
import { PercentageTrendChart } from "@/components/percentage-trend-chart";
import { api } from "@/lib/api";
import {
  formatNumber,
  formatNumberFull,
  formatPercent,
  formatRelative,
  formatPercentage,
} from "@/lib/format";

export function DashboardPage() {
  const summaryQuery = useQuery({
    queryKey: ["summary"],
    queryFn: api.summary,
  });
  const runsQuery = useQuery({
    queryKey: ["runs", 180],
    queryFn: () => api.runs(180),
  });

  const summary = summaryQuery.data;
  const runs = runsQuery.data ?? [];
  const loadingSummary = summaryQuery.isLoading;
  const loadingRuns = runsQuery.isLoading;

  const latest = summary?.latest ?? null;
  const delta = summary?.delta_daily_score ?? null;

  const deltaInfo =
    delta == null
      ? null
      : delta === 0
        ? { value: "0.00", tone: "neutral" as const }
        : delta > 0
          ? {
              value: `+${delta.toFixed(2)} vs prior day`,
              tone: "bad" as const,
            }
          : {
              value: `${delta.toFixed(2)} vs prior day`,
              tone: "good" as const,
            };

  const isError = summaryQuery.isError || runsQuery.isError;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          Antisemitism on X — daily monitoring
        </h1>
        {summary?.generated_at ? (
          <p className="text-sm text-muted-foreground/80">
            Last updated {formatRelative(summary.generated_at)}.
          </p>
        ) : null}
      </div>

      {isError ? (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="flex items-center gap-3 p-5 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              Could not load data from the API.{" "}
              {summaryQuery.error instanceof Error
                ? summaryQuery.error.message
                : runsQuery.error instanceof Error
                  ? runsQuery.error.message
                  : "Unknown error"}
            </span>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="% Antisemitic Posts Estimated"
          value={latest ? formatPercentage(latest.daily_score) : "—"}
          hint={
            latest
              ? `${formatPercent(latest.antisemitic_sample_rate)} antisemitic posts in sample`
              : "Awaiting first completed run"
          }
          icon={Flame}
          delta={deltaInfo}
          loading={loadingSummary}
        />
        <KpiCard
          label="Estimated antisemitic posts (last day)"
          value={
            latest ? formatNumber(latest.estimated_antisemitic_post_count) : "—"
          }
          hint={
            latest
              ? `of ~${formatNumber(latest.total_matching_post_count)} matching posts`
              : undefined
          }
          icon={Telescope}
          loading={loadingSummary}
        />
        <KpiCard
          label="Antisemitic posts / sample size"
          value={
            latest
              ? `${formatNumberFull(latest.antisemitic_sample_count)} / ${formatNumberFull(latest.sample_size_analyzed)}`
              : "—"
          }
          hint={
            latest
              ? `${formatNumberFull(latest.antisemitic_sample_count)} antisemitic posts in ${formatNumberFull(latest.sample_size_analyzed)} post random sample`
              : undefined
          }
          icon={Microscope}
          loading={loadingSummary}
        />
        <KpiCard
          label="Completed runs"
          value={
            summary
              ? `${formatNumberFull(summary.total_completed_runs)}`
              : "—"
          }
          hint={
            summary
              ? `${formatNumberFull(summary.total_posts_analyzed_all_time)} posts analyzed all time`
              : undefined
          }
          icon={CalendarRange}
          loading={loadingSummary}
        />
      </div>

      <PercentageTrendChart runs={runs} loading={loadingRuns} />
      <RunsTable
        runs={runs}
        loading={loadingRuns}
        emptyHint="No runs in the database yet. Run the tracker once and refresh."
      />
    </div>
  );
}
